import { useTranslation } from 'react-i18next';
// OperatorContactMenu.jsx
//
// Botón de teléfono que abre un menú con las acciones de contacto:
// llamar, WhatsApp o copiar. Nunca abre WhatsApp sin acción explícita.

import React, { useState } from 'react';
import { Chip, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  ContentCopy as CopyIcon,
  PhoneDisabled as NoPhoneIcon,
} from '@mui/icons-material';

import { useNotification } from '../common/GlobalNotifications';
import { formatPhone, telLink, whatsappLink, toE164, isValidE164 } from '../../utils/phone';

const OperatorContactMenu = ({ phone, phoneE164, name = '', size = 'small' }) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useNotification();
  const [anchorEl, setAnchorEl] = useState(null);

  // El backend entrega E.164, pero puede venir de un número histórico guardado
  // con prefijo nacional: solo se acepta si es E.164 válido, si no se renormaliza.
  const e164 = isValidE164(phoneE164) ? phoneE164 : toE164(phone);
  const hasPhone = Boolean(phone);
  const valid = Boolean(e164);

  if (!hasPhone) {
    return (
      <Chip size="small" variant="outlined" icon={<NoPhoneIcon sx={{ fontSize: 14 }} />}
        label={t('hardcoded.components_operators_operatorcontactmenu.sin_telefono')} sx={{ height: 22, fontSize: '0.7rem', color: 'text.disabled' }} />
    );
  }

  if (!valid) {
    return (
      <Tooltip title={t('hardcoded.components_operators_operatorcontactmenu.el_numero_guardado_no_tiene')}>
        <Chip size="small" variant="outlined" color="warning"
          label={t('hardcoded.components_operators_operatorcontactmenu.numero_invalido')} sx={{ height: 22, fontSize: '0.7rem' }} />
      </Tooltip>
    );
  }

  const close = () => setAnchorEl(null);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(e164);
      showSuccess(t('hardcoded.components_operators_operatorcontactmenu.numero_copiado'));
    } catch {
      showError(t('hardcoded.components_operators_operatorcontactmenu.no_se_pudo_copiar_el'));
    }
    close();
  };

  const openLink = (url) => {
    window.open(url, t('hardcoded.components_operators_operatorcontactmenu._blank'), t('hardcoded.components_operators_operatorcontactmenu.noopenernoreferrer'));
    close();
  };

  return (
    <>
      <Chip
        size="small"
        icon={<PhoneIcon sx={{ fontSize: 14 }} />}
        label={formatPhone(phone)}
        onClick={(e) => { e.stopPropagation(); setAnchorEl(e.currentTarget); }}
        sx={{ height: 24, fontSize: '0.72rem', cursor: 'pointer', fontWeight: 600 }}
      />
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 200 } } }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <Typography variant="caption" color="text.secondary">
            {name ? `${name} · ` : ''}{formatPhone(phone)}
          </Typography>
        </MenuItem>
        <MenuItem onClick={() => { window.location.href = telLink(phone); close(); }}>
          <ListItemIcon><PhoneIcon fontSize={size} /></ListItemIcon>
          <ListItemText primary="Llamar" />
        </MenuItem>
        <MenuItem onClick={() => openLink(whatsappLink(phone))}>
          <ListItemIcon><WhatsAppIcon fontSize={size} sx={{ color: '#25D366' }} /></ListItemIcon>
          <ListItemText primary="WhatsApp" />
        </MenuItem>
        <MenuItem onClick={copy}>
          <ListItemIcon><CopyIcon fontSize={size} /></ListItemIcon>
          <ListItemText primary={t('hardcoded.components_operators_operatorcontactmenu.copiar_numero')} />
        </MenuItem>
      </Menu>
    </>
  );
};

export default OperatorContactMenu;
